import { StyleSheet, View } from "react-native";
import { useHistory } from "react-router-dom";

import { Button, Typography, useTheme } from "@smartrent/ui";

import AppShell from "@/containers/AppShell";
import { StateType, useAppState } from "@/context/AppState";
import { TourType } from "@/types";
import { clearStorageStayLoggedIn } from "@/utils/storage";
import { isV2FrontendEnabled } from "@/utils/v2";

interface TourHelpProps {
  tour: TourType;
}

const TourHelpPage = ({ tour }: TourHelpProps) => {
  const isV2Frontend = isV2FrontendEnabled();
  const { colors } = useTheme();
  const history = useHistory();
  const [{ config }, setAppState] = useAppState();

  const handleReset = async () => {
    await clearStorageStayLoggedIn();

    window.location.href = `/property/${config.uuid}`;
    // Remove tour and prospect from state, to ensure they get reloaded
    // properly at the correct time. (This is a dependency of the tour page.)
    setAppState((state: StateType) => ({
      ...state,
      tour: undefined,
      prospect: undefined,
    }));
  };

  const getMessagingAndAction = (tour: TourType) => {
    if (tour.deletedAt) {
      return {
        title: "Tour Cancelled",
        content:
          "Your tour has been cancelled and can no longer be edited. Please schedule a new tour.",
        buttonLabel: "Schedule A New Tour",
        buttonAction: () => handleReset(),
      };
    }

    if (tour.isFinalized) {
      const buttonAction = isV2Frontend
        ? history.push(`/tour/${tour.token}/checklist`)
        : history.push(`/tour/${tour.token}`);
      return {
        title: "Tour Finalized",
        content: "Your tour has been finalized and can no longer be modified.",
        buttonLabel: "View Scheduled Tour",
        buttonAction: () => buttonAction,
      };
    }

    if (tour.identityVerificationResult === "rejected") {
      return {
        title: "Verification Rejected",
        content:
          "Your verification has been rejected, contact the office for further assistance.",
        buttonLabel: "Call Office",
        buttonAction: () => window.open(`tel:${config.phoneNumber}`),
      };
    }

    return {
      title: "Error",
      content: "The tour is not finalized or cancelled",
      buttonLabel: "Schedule a New Tour",
      buttonAction: () => handleReset(),
    };
  };

  const messagingAndAction = getMessagingAndAction(tour);

  return (
    <View style={styles.root}>
      <AppShell header={{ left: null }} showNewTourButton={false}>
        <View
          style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
        >
          <View style={{ alignItems: "center", padding: 16 }}>
            <Typography
              type={"title"}
              style={{ fontWeight: "600", padding: 10, textAlign: "center" }}
            >
              {messagingAndAction.title}
            </Typography>
            <Typography
              type={"title2"}
              style={{
                textAlign: "center",
                fontWeight: "400",
                paddingBottom: 32,
                color: colors.primary,
              }}
            >
              {messagingAndAction.content}
            </Typography>
            <Button
              onPress={() => messagingAndAction.buttonAction()}
              style={{ width: "80%", marginBottom: 20 }}
              variation="outlined"
              size="large"
            >
              {messagingAndAction.buttonLabel}
            </Button>
          </View>
        </View>
      </AppShell>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    width: "100%",
    height: "100%",
    position: "relative",
  },
});

export default TourHelpPage;
